import { Injectable, Inject, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface ImagePreloadItem {
  id: string;
  image: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImagePreloadService {
  private readonly imageUrlCache = new Map<string, string>();
  private readonly preloadedImageUrls = new Map<string, string>();

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  /**
   * Preloads images for a list of items
   * @param items - Array of items with id and image URL
   * @param imagesLoaded - Object to track loaded state by item id
   * @param cdr - Optional ChangeDetectorRef for manual change detection
   */
  preloadImages(
    items: ImagePreloadItem[],
    imagesLoaded: { [key: string]: boolean },
    cdr?: ChangeDetectorRef
  ): void {
    if (!isPlatformBrowser(this.platformId) || items.length === 0) {
      return;
    }

    const validItems = items.filter((item) => !!item.image?.trim());
    if (validItems.length === 0) {
      return;
    }

    // Prioritize first image - load it immediately and separately
    const firstItem = validItems[0];
    if (firstItem) {
      this.preloadFirstImage(firstItem, imagesLoaded, cdr);
    }

    // Load remaining images
    validItems.slice(1).forEach((item) => {
      this.preloadImage(item, imagesLoaded, cdr);
    });
  }

  /**
   * Preloads the first image with highest priority
   */
  private preloadFirstImage(
    item: ImagePreloadItem,
    imagesLoaded: { [key: string]: boolean },
    cdr?: ChangeDetectorRef
  ): void {
    const imageUrl = item.image;
    this.imageUrlCache.set(item.id, imageUrl);
    this.preloadedImageUrls.set(item.id, imageUrl);

    // Add link preload for first image - highest priority
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = imageUrl;
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);

    // Preload first image immediately with highest priority
    const preloadImg = new Image();
    preloadImg.fetchPriority = 'high';
    preloadImg.loading = 'eager';
    preloadImg.decoding = 'sync';
    preloadImg.src = imageUrl;

    if (preloadImg.complete && preloadImg.naturalWidth > 0 && preloadImg.naturalHeight > 0) {
      imagesLoaded[item.id] = true;
      if (cdr) {
        cdr.detectChanges();
      }
      return;
    }

    preloadImg.onload = () => {
      if (preloadImg.complete && preloadImg.naturalWidth > 0 && preloadImg.naturalHeight > 0) {
        imagesLoaded[item.id] = true;
        if (cdr) {
          cdr.detectChanges();
        }
      }
    };

    preloadImg.onerror = () => {
      imagesLoaded[item.id] = false;
    };
  }

  /**
   * Preloads a single image
   */
  private preloadImage(
    item: ImagePreloadItem,
    imagesLoaded: { [key: string]: boolean },
    cdr?: ChangeDetectorRef
  ): void {
    const imageUrl = item.image;
    this.imageUrlCache.set(item.id, imageUrl);
    this.preloadedImageUrls.set(item.id, imageUrl);

    const preloadImg = new Image();
    preloadImg.fetchPriority = 'high';
    preloadImg.loading = 'eager';
    preloadImg.decoding = 'sync';
    preloadImg.src = imageUrl;

    if (preloadImg.complete && preloadImg.naturalWidth > 0 && preloadImg.naturalHeight > 0) {
      imagesLoaded[item.id] = true;
      if (cdr) {
        cdr.detectChanges();
      }
      return;
    }

    preloadImg.onload = () => {
      if (preloadImg.complete && preloadImg.naturalWidth > 0 && preloadImg.naturalHeight > 0) {
        imagesLoaded[item.id] = true;
        if (cdr) {
          cdr.detectChanges();
        }
      }
    };

    preloadImg.onerror = () => {
      imagesLoaded[item.id] = false;
    };
  }

  /**
   * Gets the preloaded image URL for an item
   */
  getImageUrl(itemId: string): string {
    return this.preloadedImageUrls.get(itemId) ?? this.imageUrlCache.get(itemId) ?? '';
  }

  /**
   * Clears the image cache
   */
  clearCache(): void {
    this.preloadedImageUrls.clear();
    this.imageUrlCache.clear();
  }
}

